import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, TextField, Button, MenuItem, Card, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Delete, ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';


const AdminAgreementTypesPage = () => {
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedTypeValue, setSelectedTypeValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [activeTypeId, setActiveTypeId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Form values
  const [form, setForm] = useState({
    id: null,
    name: '',
    description: '', // new
    checklist: '',
    chatgpt_gemini_prompt: '',
    arbitrationPrompt: '',
  });

  const fetchTypes = async () => {
    try {
      const res = await fetch('https://triunityiq.com/api.php/get-agreement-types');
      const data = await res.json();
      if (data.types) {
        console.log('data.types ',data.types);
        setTypes(data.types || []);
      } else {
        setMsg({ type: 'error', text: data.msg || 'Failed to fetch agreement types.' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = () => {
    if (!selectedTypeValue) return;
    setConfirmDeleteOpen(true);
  };


  const handleAddType = async () => {
    if (!form.name) return;
    setLoading(true);
    await fetch('https://triunityiq.com/api.php/create-agreement-type', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        description: form.description
      }),
    });
    setForm({ name: '', description: '', checklist: '', chatgpt_gemini_prompt: '', arbitrationPrompt: '' });
    setLoading(false);
    setOpenModal(false);
    fetchTypes();
  };

  const handleSaveChanges = async () => {
    if (!activeTypeId) return;
    setLoading(true);
    await fetch('https://triunityiq.com/api.php/update-agreement-type', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: activeTypeId,
        checklist: form.checklist,
        chatgpt_gemini_prompt: form.chatgpt_gemini_prompt,
        arbitration_prompt: form.arbitrationPrompt
      }),
    });
    setLoading(false);
    fetchTypes();
    alert('Changes saved!');
  };

  const handleSelectType = (typeId) => {
    const selected = types.find(t => t.id === typeId);
    if (selected) {
      setActiveTypeId(selected.id);
      setForm({
        id: selected.id, // ✅ Add this
        name: selected.name,
        description: selected.description,
        checklist: selected.checklist || '',
        chatgpt_gemini_prompt: selected.chatgpt_gemini_prompt || '',
        arbitrationPrompt: selected.arbitration_prompt || ''
      });
    }
  };

  const filteredTypes = (types || []).filter((t) => {
    const label = (t?.agreement_type ?? '').toString();
    return label.toLowerCase().includes(search.toLowerCase());
  });

  useEffect(() => {
    if (!activeTypeId) return;

    const timeout = setTimeout(() => {
      fetch('https://triunityiq.com/api.php/update-agreement-type', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: activeTypeId,
          checklist: form.checklist,
          chatgpt_gemini_prompt: form.chatgpt_gemini_prompt,
          arbitration_prompt: form.arbitrationPrompt
        }),
      });
    }, 1000); // 1s debounce

    return () => clearTimeout(timeout);
  }, [form, activeTypeId]);

  const handleConfirmDelete = async () => {
    try {
      await fetch('https://triunityiq.com/api.php/delete-agreement-type', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedTypeValue }),
      });

      const updatedTypes = types.filter(t => t.id !== selectedTypeValue);
      setTypes(updatedTypes);

      // Reset form and selection
      setSelectedTypeValue("");
      setActiveTypeId(null);
      setForm({
        name: '',
        description: '',
        checklist: '',
        chatgpt_gemini_prompt: '',
        arbitrationPrompt: '',
      });

      setConfirmDeleteOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handleFieldChange = (field, value) => {
    // Update form state (local)
    setForm(prev => ({ ...prev, [field]: value }));

    // Update the master types list
    setTypes(prevTypes =>
      prevTypes.map(type =>
        type.id === form.id ? { ...type, [field]: value } : type
      )
    );

  };
  const selectedTypeName = types.find(t => t.id === selectedTypeValue)?.name || '';
  return (
    <Container className="container">
      <Button variant="outlined" startIcon={<ArrowBack />} component={Link} to="/">
        Go to Dashboard
      </Button>

      <Typography variant="h5" sx={{ mt: 3, mb: 2 }} color="primary">
        Manage Agreement Types
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
        <TextField
          select
          label="Select Agreement Type"
          value={selectedTypeValue}
          onChange={(e) => {
            const selectedId = e.target.value;
            setSelectedTypeValue(selectedId);
            handleSelectType(selectedId);
          }}
          fullWidth
        >
          {filteredTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.agreement_type}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="🔍 Search Agreement Type"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => setOpenModal(true)}
            sx={{ bgcolor: '#333', color: '#fff', '&:hover': { bgcolor: '#222' } }}
          >
            [+ Add Type]
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteSelected}
            disabled={selectedTypeValue === ""} // ✅ DISABLED if no type selected
          >
            Delete Selected
          </Button>
        </Box>
      </Box>

      {/* Add new agreement type form */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Add New Agreement Type
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          {/* <TextField
            label="Agreement Type Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />
 */}
          {/* Horizontal box for 3 fields */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Checklist"
              multiline
              rows={30}
              value={form.checklist}
              onChange={(e) => handleFieldChange("checklist", e.target.value)}
              fullWidth
              sx={{ flex: 1, minWidth: 300 }}
            />

            <TextField
              label="Gemini / ChatGPT Prompt"
              multiline
              rows={30}
              value={form.chatgpt_gemini_prompt}
              onChange={(e) => handleFieldChange("chatgpt_gemini_prompt", e.target.value)}
              fullWidth
              sx={{ flex: 1, minWidth: 300 }}
            />

            <TextField
              label="Arbitration Prompt"
              multiline
              rows={30}
              value={form.arbitrationPrompt}
              onChange={(e) => handleFieldChange("arbitrationPrompt", e.target.value)}
              fullWidth
              sx={{ flex: 1, minWidth: 300 }}
            />
          </Box>
        </Box>
      </Card>

      {/* List of agreement types */}
      {/* <Card sx={{ p: 2 }}>
        {filteredTypes.map((type) => (
          <Box
            key={type.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #ddd',
              py: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedIds.includes(type.id)}
                  onChange={() => handleSelect(type.id)}
                />
              }
              label={type.name}
              sx={{ flex: 1 }}
            />
            <Typography variant="body2" sx={{ color: '#555' }}>
              Edited: {type.last_edited || 'Unknown'}
            </Typography>
          </Box>
        ))}
      </Card> */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
        <Button variant="contained" color="primary" onClick={handleSaveChanges}>
          Publish
        </Button>
      </Box>
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
        <DialogTitle>Create New Agreement Type</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Agreement Type Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            style={{ marginTop: '6px' }}
            fullWidth
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddType}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete this agreement type?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove <strong>{selectedTypeName}</strong> from ClauseBot. Published versions will no longer be available for new reviews. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminAgreementTypesPage;
